import { deliveryAreaService } from "@/services/DeliveryAreaService";
import { toast } from "vue3-toastify";
import { i18n } from "./../lang";

const state = {
    deliveryAreas: [],
    editingDeliveryArea: null,
    isAddingDeliveryArea: false,
    getDeliveryAreaRequest: {
        loading: false
    },
    saveDeliveryAreaRequest: {
        loading: false
    },
    deleteDeliveryAreaRequest: {
        loading: false
    }
}

const mutations = {
    setDeliveryAreas(state, deliveryAreas) {
        state.deliveryAreas = [...deliveryAreas].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
    },
    setEditingDeliveryArea(state, deliveryArea) {
        state.editingDeliveryArea = deliveryArea;
    },
    setIsAddingDeliveryArea(state, isAdding) {
        state.isAddingDeliveryArea = isAdding;
    },
    setGetDeliveryAreaRequest(state, {loading, message}) {
        state.getDeliveryAreaRequest = {loading: loading, message: message};
    },
    setSaveDeliveryAreaRequest(state, {loading, message}) {
        state.saveDeliveryAreaRequest = {loading: loading, message: message};
    },
    setDeleteDeliveryAreaRequest(state, {loading, message}) {
        state.deleteDeliveryAreaRequest = {loading: loading, message: message};
    }
}

const actions = {
    getDeliveryAreas({commit}) {
        commit('setGetDeliveryAreaRequest', {loading: true});
        return deliveryAreaService.getDeliveryAreas()
            .then((response) => {
                commit('setGetDeliveryAreaRequest', {loading: false});
                commit('setDeliveryAreas', response.data || []);
            })
            .catch((error) => {
                commit('setGetDeliveryAreaRequest', {loading: false, message: error});
            });
    },
    addDeliveryArea({commit, state}, deliveryArea) {
        commit('setSaveDeliveryAreaRequest', {loading: true});
        return deliveryAreaService.addDeliveryArea(deliveryArea)
            .then((response) => {
                commit('setSaveDeliveryAreaRequest', {loading: false});
                commit('setDeliveryAreas', [...state.deliveryAreas, response.data]);
                commit('setEditingDeliveryArea', null);
                toast.success(i18n.global.t("toast.createSuccess"));
                return response.data;
            })
            .catch((error) => {
                commit('setSaveDeliveryAreaRequest', {loading: false, message: error});
                toast.error(typeof error === 'string' ? error : i18n.global.t("toast.createError"));
                return Promise.reject(error);
            });
    },
    async addDeliveryAreas({commit, state}, deliveryAreas) {
        commit('setSaveDeliveryAreaRequest', {loading: true});
        const created = [];

        try {
            for (const deliveryArea of deliveryAreas) {
                const response = await deliveryAreaService.addDeliveryArea(deliveryArea);
                created.push(response.data);
            }
        } catch (error) {
            commit('setSaveDeliveryAreaRequest', {loading: false, message: error});

            if (created.length) {
                commit('setDeliveryAreas', [...state.deliveryAreas, ...created]);

                const remaining = deliveryAreas.slice(created.length);
                if (state.editingDeliveryArea && remaining.length) {
                    commit('setEditingDeliveryArea', {
                        ...state.editingDeliveryArea,
                        name: remaining.length === 1 ? remaining[0].name : '',
                        coordinates: remaining[0].coordinates,
                        batch: remaining.length > 1 ? remaining : null,
                    });
                }
            }

            toast.error(typeof error === 'string' ? error : i18n.global.t("toast.createError"));
            return Promise.reject(error);
        }

        commit('setSaveDeliveryAreaRequest', {loading: false});
        commit('setDeliveryAreas', [...state.deliveryAreas, ...created]);
        commit('setEditingDeliveryArea', null);
        toast.success(i18n.global.t("toast.createSuccess"));
        return created;
    },
    updateDeliveryArea({commit, state}, deliveryArea) {
        commit('setSaveDeliveryAreaRequest', {loading: true});
        return deliveryAreaService.updateDeliveryArea(deliveryArea)
            .then((response) => {
                commit('setSaveDeliveryAreaRequest', {loading: false});
                const updatedList = state.deliveryAreas.map(
                    (area) => area.deliveryAreaId === response.data.deliveryAreaId ? response.data : area
                );
                commit('setDeliveryAreas', updatedList);
                commit('setEditingDeliveryArea', null);
                toast.success(i18n.global.t("toast.successEdit"));
                return response.data;
            })
            .catch((error) => {
                commit('setSaveDeliveryAreaRequest', {loading: false, message: error});
                toast.error(typeof error === 'string' ? error : i18n.global.t("toast.errorEdit"));
                return Promise.reject(error);
            });
    },
    deleteDeliveryArea({commit, state}, deliveryArea) {
        commit('setDeleteDeliveryAreaRequest', {loading: true});
        return deliveryAreaService.deleteDeliveryArea(deliveryArea)
            .then(() => {
                commit('setDeleteDeliveryAreaRequest', {loading: false});
                const updatedList = state.deliveryAreas.filter(
                    (area) => area.deliveryAreaId !== deliveryArea.deliveryAreaId
                );
                commit('setDeliveryAreas', updatedList);
                toast.success(i18n.global.t("toast.deleteSuccess"));
            })
            .catch((error) => {
                commit('setDeleteDeliveryAreaRequest', {loading: false, message: error});
                toast.error(typeof error === 'string' ? error : i18n.global.t("toast.deleteError"));
            });
    }
}

export const deliveryArea = {
    namespaced: true,
    state,
    actions,
    mutations,
}
